import { useEffect, useState } from 'react';
import { Alert, App, Col, Form, Input, Modal, Row, Select, Typography } from 'antd';
import type {
  FleetStatus,
  FleetVehicle,
  FleetVehicleCreateRequest,
  FleetVehicleUpdateRequest,
} from '@goelectric/shared-types';
import { fleetApi } from '../api/fleetApi';
import { describeFleetApiError } from '../utils/fleetErrors';
import {
  FLEET_INACTIVE_HINT,
  FLEET_RECONCILIATION_MESSAGE,
  requiresFleetReconciliation,
} from '../utils/fleetMessages';
import { FLEET_VEHICLE_STATUS_LABELS } from '../utils/format';

/** UID físico ISO14443: exactamente 8 o 14 caracteres hexadecimales (contrato). */
export const RFID_PATTERN = /^(?:[0-9A-Fa-f]{8}|[0-9A-Fa-f]{14})$/;

const STATUS_OPTIONS = (['ACTIVE', 'INACTIVE'] as const).map((value) => ({
  value,
  label: FLEET_VEHICLE_STATUS_LABELS[value],
}));

export interface VehicleFormState {
  plate: string;
  rfidTag?: string;
  brand?: string;
  model?: string;
  status: FleetStatus;
}

/** Normaliza el UID para compararlo/guardarlo (hex en mayúsculas, sin espacios). */
export function normalizeRfid(value: string): string {
  return value.trim().toUpperCase();
}

/** `true` si el valor es un UID ISO14443 válido (8 o 14 hex). */
export function isValidRfid(value: string): boolean {
  return RFID_PATTERN.test(normalizeRfid(value));
}

/** Compone el cuerpo del alta (`FleetVehicleCreateRequest`). */
export function buildVehicleCreateBody(values: VehicleFormState): FleetVehicleCreateRequest {
  const body: FleetVehicleCreateRequest = {
    plate: values.plate.trim().toUpperCase(),
    rfidTag: normalizeRfid(values.rfidTag ?? ''),
    rfidType: 'ISO14443',
    status: values.status,
  };
  const brand = values.brand?.trim();
  if (brand) body.brand = brand;
  const model = values.model?.trim();
  if (model) body.model = model;
  return body;
}

/** Compone el cuerpo del PATCH; el `rfidTag` es inmutable en #49 y NO viaja. */
export function buildVehicleUpdateBody(values: VehicleFormState): FleetVehicleUpdateRequest {
  const body: FleetVehicleUpdateRequest = {
    plate: values.plate.trim().toUpperCase(),
    status: values.status,
  };
  const brand = values.brand?.trim();
  if (brand) body.brand = brand;
  const model = values.model?.trim();
  if (model) body.model = model;
  return body;
}

export interface VehicleFormModalProps {
  open: boolean;
  fleetId: string;
  /** Vehículo a editar; `null` para el alta. */
  vehicle: FleetVehicle | null;
  /** La flota está inactiva: no se permite reactivar el vehículo desde la edición. */
  fleetInactive?: boolean;
  onClose: () => void;
  /** Refresca la tabla de vehículos y la flota tras guardar. */
  onSaved: () => void;
}

/**
 * Alta/edición de vehículo de una flota (US-2.3, ADR 0047).
 *
 * El `rfidTag` es el UID físico ISO14443 (8 o 14 hex), no un identificador compuesto.
 * En edición es **solo lectura**: para cambiar la tarjeta se da de baja el vehículo y se
 * crea uno nuevo (contrato `FleetVehicleUpdateRequest`).
 */
export function VehicleFormModal({
  open,
  fleetId,
  vehicle,
  fleetInactive = false,
  onClose,
  onSaved,
}: VehicleFormModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<VehicleFormState>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEdit = vehicle !== null;

  useEffect(() => {
    if (!open) {
      return;
    }
    setError(null);
    form.resetFields();
    if (vehicle) {
      form.setFieldsValue({
        plate: vehicle.plate,
        rfidTag: vehicle.rfidTag,
        brand: vehicle.brand,
        model: vehicle.model,
        status: vehicle.status,
      });
    } else {
      form.setFieldsValue({ status: 'ACTIVE', rfidTag: '' });
    }
  }, [open, vehicle, form]);

  const handleFinish = async (values: VehicleFormState) => {
    setSubmitting(true);
    setError(null);
    try {
      const saved = vehicle
        ? await fleetApi.updateVehicle(fleetId, vehicle.id, buildVehicleUpdateBody(values))
        : await fleetApi.createVehicle(fleetId, buildVehicleCreateBody(values));
      // El alta/edición es fail-safe: si la replicación cross-tenant queda parcial o
      // con error, el backend responde igualmente con `authorizationStatus` a reconciliar.
      if (requiresFleetReconciliation(saved.authorizationStatus)) {
        message.warning(FLEET_RECONCILIATION_MESSAGE);
      } else {
        message.success(
          vehicle ? 'Vehículo actualizado.' : 'Vehículo agregado a la flota.',
        );
      }
      onSaved();
      onClose();
    } catch (caught) {
      const { message: detail, field } = describeFleetApiError(
        caught,
        vehicle ? 'No se pudo actualizar el vehículo.' : 'No se pudo agregar el vehículo.',
      );
      setError(detail);
      if (field) {
        form.setFields([{ name: field as keyof VehicleFormState, errors: [detail] }]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? 'Editar vehículo' : 'Agregar vehículo'}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={isEdit ? 'Guardar' : 'Agregar'}
      cancelText="Cancelar"
      confirmLoading={submitting}
      width={600}
      destroyOnHidden
    >
      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="No se pudo guardar el vehículo"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      {fleetInactive && (
        <Alert
          type="warning"
          showIcon
          role="status"
          message={FLEET_INACTIVE_HINT}
          description="No se puede reactivar el vehículo mientras la flota esté inactiva."
          style={{ marginBottom: 16 }}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleFinish} autoComplete="off">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Placa"
              name="plate"
              rules={[{ required: true, message: 'Ingresa la placa del vehículo.' }]}
            >
              <Input
                placeholder="Ej. ABC123"
                style={{ fontFamily: 'var(--ge-font-mono)', textTransform: 'uppercase' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Estado"
              name="status"
              rules={[{ required: true, message: 'Selecciona el estado del vehículo.' }]}
              extra={fleetInactive ? 'La flota está inactiva: el estado no se puede cambiar.' : undefined}
            >
              <Select<FleetStatus> options={STATUS_OPTIONS} disabled={fleetInactive} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Tarjeta RFID (UID ISO14443)"
          name="rfidTag"
          extra={
            isEdit
              ? 'El UID es inmutable: para cambiar la tarjeta, da de baja el vehículo y crea uno nuevo.'
              : 'UID físico de 8 o 14 caracteres hexadecimales (0-9, A-F), sin prefijo de flota.'
          }
          rules={
            isEdit
              ? undefined
              : [
                  { required: true, message: 'Ingresa el UID de la tarjeta RFID.' },
                  {
                    validator: (_, value: string | undefined) =>
                      value && isValidRfid(value)
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error('El UID debe tener 8 o 14 caracteres hexadecimales.'),
                          ),
                  },
                ]
          }
        >
          <Input
            disabled={isEdit}
            placeholder="Ej. 04A1B2C3"
            style={{ fontFamily: 'var(--ge-font-mono)', textTransform: 'uppercase' }}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Marca" name="brand">
              <Input placeholder="Ej. Renault" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Modelo" name="model">
              <Input placeholder="Ej. Kangoo Z.E." />
            </Form.Item>
          </Col>
        </Row>

        <Typography.Text type="secondary">
          El servidor replica la autorización RFID en los tenants de CitrineOS activos.
        </Typography.Text>
      </Form>
    </Modal>
  );
}
